import {
  Component, computed, effect, ElementRef, HostListener, inject, NgZone, signal, viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  LucideAngularModule, History, SquarePen, X, SendHorizontal, FileText, Loader, ClipboardList, Check, ShieldCheck,
  CircleCheckBig, ExternalLink, TriangleAlert, MessageSquare, BellRing,
} from 'lucide-angular';
import {
  MaquiService, MaquiSource, MaquiAction, MaquiConfirmarResponse, MaquiConversacionItem, MaquiNudge,
} from '../../../core/services/maqui.service';
import { MaquiUiService } from '../../../core/services/maqui-ui.service';
import { InstanceConfigService } from '../../../core/services/instance-config.service';
import { MaquiMarkdownPipe } from '../../pipes/maqui-markdown.pipe';
import {
  MaquiAccionInfo, accionInfo, avisoDeUso, resultadoIncierto, sugerenciasBienvenida, textoBienvenida, textoCoach,
} from './maqui-acciones';

/** Estado de una acción propuesta dentro de un mensaje. */
type EstadoAccion = 'propuesta' | 'confirmando' | 'exito' | 'error' | 'cancelado';

/** Un mensaje del hilo de Maqui (usuario o asistente). */
interface MaquiMsg {
  rol: 'user' | 'assistant';
  texto: string;
  sources?: MaquiSource[];
  /** Respuestas rápidas (quick replies) data-backed para una pregunta aclaratoria. */
  sugerencias?: string[];
  accion?: MaquiAction;
  estado?: EstadoAccion;
  resultado?: MaquiConfirmarResponse;
  /**
   * La confirmación falló por red o servidor: la acción pudo haberse hecho sin que llegara la respuesta. No se ofrece
   * reintentar —emitiría una segunda guía, un segundo correo o un segundo pago—; se manda a revisar.
   */
  incierto?: boolean;
}

/**
 * Asistente IA "Maqui" (issue #648) — drawer lateral derecho + FAB. Responde
 * preguntas grounded con citación de fuentes. Se auto-oculta si Maqui no está
 * habilitado en la instancia (flag MAQUI_ENABLED). Diseño DS v16.
 *
 * F2 (esta entrega): launcher FAB, bienvenida + chips, hilo, respuesta grounded
 * con chips de fuente (deep-link), estado "consultando". Historial, nudge,
 * onboarding, streaming SSE y la acción de crear guía llegan en fases siguientes.
 */
@Component({
  selector: 'sr-maqui',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, MaquiMarkdownPipe],
  template: `
    @if (visible()) {
      <!-- Launcher FAB -->
      @if (!open()) {
        <button type="button" class="sr-maqui-fab"
                [class.sr-maqui-fab--pulse]="showCoach()"
                (click)="abrir()"
                [attr.data-tip]="tip()" aria-label="Pregúntale a Maqui">
          <img class="sr-maqui-fab__img" src="assets/maqui/casquito.png" alt="Maqui" />
        </button>
        @if (showNudge()) { <span class="sr-maqui-fab-dot" aria-hidden="true"></span> }
      }

      <!-- Onboarding coach (primera vez) -->
      @if (showCoach()) {
        <div class="sr-maqui-coach" role="dialog" aria-label="Conoce a Maqui">
          <span class="sr-maqui-coach__tag">NUEVO</span>
          <div class="sr-maqui-coach__title">Conoce a Maqui</div>
          <p class="sr-maqui-coach__text">
            {{ coach() }} Responde con datos reales de tu cuenta.
          </p>
          <button type="button" class="sr-maqui-coach__btn" (click)="dismissCoach()">Entendido</button>
        </div>
      }

      <!-- Drawer teaser "Próximamente" (modo comingSoon: visible pero sin chat) -->
      @if (open() && comingSoon()) {
        <div class="sr-maqui-backdrop" (click)="cerrar()" aria-hidden="true"></div>
        <div class="sr-maqui sr-maqui--teaser" role="dialog" aria-label="Maqui — próximamente">
          <header class="sr-maqui__head">
            <div class="sr-maqui__brand">
              <img class="sr-maqui__avatar" src="assets/maqui/casquito.png" alt="Maqui" />
              <div class="sr-maqui__brandtxt">
                <div class="sr-maqui__title">
                  Maqui <span class="sr-maqui__beta">PRÓXIMAMENTE</span>
                </div>
                <div class="sr-maqui__subtitle">Tu asistente de SoftRent</div>
              </div>
            </div>
            <div class="sr-maqui__actions">
              <button type="button" class="sr-maqui__iconbtn" (click)="cerrar()"
                      title="Cerrar" aria-label="Cerrar">
                <lucide-angular [img]="icons.X" [size]="16"></lucide-angular>
              </button>
            </div>
          </header>
          <div class="sr-maqui__teaser">
            <img class="sr-maqui__teaser-img" src="assets/maqui/casquito.png" alt="" aria-hidden="true" />
            <h3 class="sr-maqui__teaser-title">Maqui está en camino</h3>
            <p class="sr-maqui__teaser-text">
              Muy pronto vas a poder pedirle en lenguaje natural la deuda de un cliente, qué equipos
              están en una obra, o que arme una guía o un recordatorio de pago, todo con datos reales
              de tu cuenta.
            </p>
            <span class="sr-maqui__teaser-badge">Próximamente</span>
          </div>
        </div>
      }

      <!-- Drawer (chat completo — solo en modo ON) -->
      @if (open() && !comingSoon()) {
        <!-- Backdrop: solo en móvil (en desktop el drawer no tapa la app) -->
        <div class="sr-maqui-backdrop" (click)="cerrar()" aria-hidden="true"></div>

        <!-- Preview de fuente (peek al pasar el mouse por un chip de fuente) -->
        @if (srcPeek(); as peek) {
          <div class="sr-maqui__peek" [style.top.px]="peek.top" [style.left.px]="peek.left">
            <span class="sr-maqui__peek-tipo">{{ tipoLabel(peek.src.entityType) }}</span>
            <span class="sr-maqui__peek-label">{{ peek.src.label || peek.src.folio }}</span>
            @if (peek.src.folio && peek.src.label) {
              <span class="sr-maqui__peek-folio">{{ peek.src.folio }}</span>
            }
            <span class="sr-maqui__peek-cta">{{ peek.src.ruta ? 'Clic para abrir la pantalla' : 'Clic para abrir el detalle' }}</span>
          </div>
        }

        <div class="sr-maqui" role="dialog" aria-label="Asistente Maqui">
          <!-- Header -->
          <header class="sr-maqui__head">
            <div class="sr-maqui__brand">
              <img class="sr-maqui__avatar" src="assets/maqui/casquito.png" alt="Maqui" />
              <div class="sr-maqui__brandtxt">
                <div class="sr-maqui__title">
                  Maqui <span class="sr-maqui__beta">BETA</span>
                </div>
                <div class="sr-maqui__subtitle">Tu asistente de SoftRent</div>
              </div>
            </div>
            <div class="sr-maqui__actions">
              <button type="button" class="sr-maqui__iconbtn"
                      [class.sr-maqui__iconbtn--on]="view() === 'history'"
                      (click)="toggleHistorial()" title="Historial" aria-label="Historial">
                <lucide-angular [img]="icons.History" [size]="16"></lucide-angular>
              </button>
              <button type="button" class="sr-maqui__iconbtn" (click)="nueva()"
                      title="Nueva conversación" aria-label="Nueva conversación">
                <lucide-angular [img]="icons.SquarePen" [size]="16"></lucide-angular>
              </button>
              <button type="button" class="sr-maqui__iconbtn" (click)="cerrar()"
                      title="Cerrar" aria-label="Cerrar">
                <lucide-angular [img]="icons.X" [size]="16"></lucide-angular>
              </button>
            </div>
          </header>

          <!-- Indicador de uso mensual (visibilidad de costo, solo cerca del tope) -->
          @if (avisoUso(); as aviso) {
            <div class="sr-maqui__uso" [class.sr-maqui__uso--warn]="aviso.alerta" role="status">
              {{ aviso.texto }}
            </div>
          }

          <!-- Body -->
          <div #body class="sr-maqui__body">
            @if (view() === 'history') {
              <!-- Historial -->
              <div class="sr-maqui__history">
                @if (conversaciones().length === 0) {
                  <div class="sr-maqui__hist-empty">Todavía no tienes conversaciones guardadas.</div>
                } @else {
                  @for (c of conversaciones(); track c.id) {
                    <button type="button" class="sr-maqui__hist-item" (click)="cargarConversacion(c)">
                      <lucide-angular class="sr-maqui__hist-ic" [img]="icons.MessageSquare" [size]="15"></lucide-angular>
                      <span class="sr-maqui__hist-title">{{ c.titulo }}</span>
                    </button>
                  }
                }
              </div>
            } @else if (mensajes().length === 0) {
              <!-- Bienvenida -->
              <div class="sr-maqui__welcome">
                <img class="sr-maqui__welcome-avatar" src="assets/maqui/casquito.png" alt="Maqui" />
                <div class="sr-maqui__welcome-title">{{ saludo() }}</div>
                <p class="sr-maqui__welcome-text">{{ bienvenida() }}</p>

                <!-- Nudge proactivo (señal de negocio del día) -->
                @if (showNudge()) {
                  <div class="sr-maqui__nudge">
                    <lucide-angular class="sr-maqui__nudge-ic" [img]="icons.BellRing" [size]="16"></lucide-angular>
                    <div class="sr-maqui__nudge-body">
                      <p class="sr-maqui__nudge-text">{{ nudge()!.texto }}</p>
                      <button type="button" class="sr-maqui__nudge-cta" (click)="verNudge()">
                        {{ nudge()!.ctaLabel }}
                        <lucide-angular [img]="icons.ExternalLink" [size]="12"></lucide-angular>
                      </button>
                    </div>
                    <button type="button" class="sr-maqui__nudge-x" (click)="descartarNudge()" aria-label="Descartar aviso">
                      <lucide-angular [img]="icons.X" [size]="14"></lucide-angular>
                    </button>
                  </div>
                }

                <div class="sr-maqui__chips">
                  @for (s of sugerencias(); track s.texto) {
                    <button type="button" class="sr-maqui__chip" (click)="enviar(s.texto)">
                      <lucide-angular class="sr-maqui__chip-ic" [img]="s.icon" [size]="15"></lucide-angular>
                      <span>{{ s.texto }}</span>
                    </button>
                  }
                </div>
                <p class="sr-maqui__trust">
                  Maqui responde solo con datos de tu cuenta y siempre cita la fuente.
                </p>
              </div>
            } @else {
              <!-- Hilo -->
              @for (m of mensajes(); track $index) {
                @if (m.rol === 'user') {
                  <div class="sr-maqui__row sr-maqui__row--user">
                    <div class="sr-maqui__bubble">{{ m.texto }}</div>
                  </div>
                } @else if (m.texto || m.sources?.length || m.accion || m.estado) {
                  <div class="sr-maqui__row sr-maqui__row--bot">
                    <img class="sr-maqui__msg-avatar" src="assets/maqui/casquito.png" alt="Maqui" />
                    <div class="sr-maqui__msg">
                      @if (m.texto) {
                        <div class="sr-maqui__msg-text" [innerHTML]="m.texto | maquiMd"></div>
                      }
                      @if (m.sources && m.sources.length && !m.sugerencias?.length) {
                        <div class="sr-maqui__sources">
                          <span class="sr-maqui__sources-label">FUENTES</span>
                          @for (src of m.sources; track src.entityType + (src.entityId ?? src.ruta)) {
                            <button type="button" class="sr-maqui__source" (click)="abrirFuente(src)"
                                    (mouseenter)="peekFuente(src, $event)" (mouseleave)="peekFuente(null)">
                              <lucide-angular class="sr-maqui__source-ic" [img]="icons.FileText" [size]="11"></lucide-angular>
                              <span>{{ src.folio || src.label }}</span>
                            </button>
                          }
                        </div>
                      }

                      <!-- Quick replies (respuestas rápidas) para una pregunta aclaratoria -->
                      @if (m.sugerencias && m.sugerencias.length) {
                        <div class="sr-maqui__quick">
                          @for (s of m.sugerencias; track s) {
                            <button type="button" class="sr-maqui__quick-chip" (click)="responder(m, s)"
                                    [disabled]="consultando()">{{ s }}</button>
                          }
                        </div>
                      }

                      <!-- Confirm card (acción propuesta) -->
                      @if (m.accion && m.estado === 'propuesta') {
                        <div class="sr-maqui__confirm">
                          <div class="sr-maqui__confirm-head">
                            <lucide-angular [img]="icons.ClipboardList" [size]="15"></lucide-angular>
                            <span class="sr-maqui__confirm-title">{{ m.accion.titulo }}</span>
                            <span class="sr-maqui__confirm-badge">BORRADOR</span>
                          </div>
                          <div class="sr-maqui__confirm-rows">
                            @for (row of m.accion.resumen; track row.label) {
                              <div class="sr-maqui__confirm-row">
                                <span class="sr-maqui__confirm-k">{{ row.label }}</span>
                                <span class="sr-maqui__confirm-v">{{ row.valor }}</span>
                              </div>
                            }
                          </div>
                          <div class="sr-maqui__confirm-actions">
                            <button type="button" class="sr-maqui__btn sr-maqui__btn--primary"
                                    (click)="confirmarAccion(m)">
                              <lucide-angular [img]="icons.Check" [size]="15"></lucide-angular>
                              {{ info(m.accion.tipo).boton }}
                            </button>
                            <button type="button" class="sr-maqui__btn sr-maqui__btn--ghost"
                                    (click)="cancelarAccion(m)">Cancelar</button>
                          </div>
                          <p class="sr-maqui__confirm-note">
                            <lucide-angular [img]="icons.ShieldCheck" [size]="12"></lucide-angular>
                            Maqui no hace nada sin tu confirmación.
                          </p>
                        </div>
                      }

                      @if (m.estado === 'confirmando') {
                        <div class="sr-maqui__consulting">
                          <lucide-angular class="sr-maqui__consulting-ic" [img]="icons.Loader" [size]="14"></lucide-angular>
                          <span>{{ info(m.accion?.tipo).enCurso }}</span>
                        </div>
                      }

                      <!-- Success card -->
                      @if (m.estado === 'exito' && m.resultado) {
                        <div class="sr-maqui__success">
                          <div class="sr-maqui__success-head">
                            <lucide-angular [img]="icons.CircleCheckBig" [size]="16"></lucide-angular>
                            <span>{{ m.resultado.mensaje }}</span>
                          </div>
                          @if (m.resultado.entityType && m.resultado.entityId) {
                            <button type="button" class="sr-maqui__source" (click)="abrirDocumento(m.resultado)">
                              <lucide-angular class="sr-maqui__source-ic" [img]="icons.ExternalLink" [size]="11"></lucide-angular>
                              <span>Abrir {{ m.resultado.numeroDocumento }}</span>
                            </button>
                          }
                        </div>
                      }

                      <!-- Warn card (bloqueo / cancelado) -->
                      @if (m.estado === 'error' && m.resultado) {
                        <div class="sr-maqui__warn">
                          <lucide-angular [img]="icons.TriangleAlert" [size]="15"></lucide-angular>
                          <span>{{ m.resultado.mensaje }}</span>
                        </div>
                        @if (m.incierto) {
                          <button type="button" class="sr-maqui__source sr-maqui__revisar" (click)="revisar(m)">
                            <lucide-angular class="sr-maqui__source-ic" [img]="icons.ExternalLink" [size]="11"></lucide-angular>
                            <span>{{ info(m.accion?.tipo).revisarLabel }}</span>
                          </button>
                        }
                      }
                      @if (m.estado === 'cancelado') {
                        <div class="sr-maqui__cancel">Cancelaste la acción. Si quieres, la preparamos de nuevo.</div>
                      }
                    </div>
                  </div>
                }
              }
              @if (consultando()) {
                <div class="sr-maqui__row sr-maqui__row--bot">
                  <img class="sr-maqui__msg-avatar" src="assets/maqui/casquito.png" alt="Maqui" />
                  <div class="sr-maqui__typing" aria-label="Maqui está pensando">
                    <span class="sr-maqui__dot"></span>
                    <span class="sr-maqui__dot"></span>
                    <span class="sr-maqui__dot"></span>
                  </div>
                </div>
              }
            }
          </div>

          <!-- Footer -->
          <footer class="sr-maqui__foot">
            <div class="sr-maqui__inputwrap">
              <input #input class="sr-maqui__input" type="text"
                     [ngModel]="texto()" (ngModelChange)="texto.set($event)"
                     placeholder="Escribe tu pregunta…" (keydown.enter)="enviar(texto())"
                     [disabled]="consultando()" />
              <button type="button" class="sr-maqui__send" (click)="enviar(texto())"
                      [class.sr-maqui__send--on]="texto().trim().length > 0 && !consultando()"
                      [disabled]="!texto().trim() || consultando()" aria-label="Enviar">
                <lucide-angular [img]="icons.SendHorizontal" [size]="16"></lucide-angular>
              </button>
            </div>
            <p class="sr-maqui__hint">Pregúntame o pídeme una acción</p>
          </footer>
        </div>
      }
    }
  `,
  styleUrl: './sr-maqui.component.scss',
})
export class SrMaquiComponent {
  private readonly maqui = inject(MaquiService);
  private readonly ui = inject(MaquiUiService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly instancia = inject(InstanceConfigService);

  /** Habilitado + abierto viven en el servicio compartido (FAB + pill del topbar). */
  readonly enabled = this.ui.enabled;
  /** El launcher se muestra (teaser o chat). */
  readonly visible = this.ui.visible;
  /** Modo teaser "Próximamente": se muestra pero sin chat funcional. */
  readonly comingSoon = this.ui.comingSoon;
  readonly open = this.ui.open;
  /** Uso del mes (consultas y gasto). */
  readonly uso = this.ui.uso;
  /** Aviso de uso: cerca del tope de consultas o de gasto, o ya alcanzado. Null = no se muestra. */
  readonly avisoUso = computed(() => avisoDeUso(this.uso()));

  readonly icons = {
    History, SquarePen, X, SendHorizontal, FileText, Loader,
    ClipboardList, Check, ShieldCheck, CircleCheckBig, ExternalLink, TriangleAlert,
    MessageSquare, BellRing,
  };

  /** Módulos de la cuenta que cambian lo que Maqui ofrece (una cuenta solo de taller no tiene arriendo). */
  private readonly modulos = computed(() => ({
    arriendo: this.instancia.muestraArriendo(),
    emiteDte: this.instancia.emiteDte(),
  }));
  readonly sugerencias = computed(() => sugerenciasBienvenida(this.modulos()));
  readonly bienvenida = computed(() => textoBienvenida(this.modulos()));
  readonly coach = computed(() => textoCoach(this.modulos()));

  /** Botón, texto en curso y pantalla de revisión de una acción. */
  info(tipo: string | null | undefined): MaquiAccionInfo {
    return accionInfo(tipo, this.modulos());
  }

  /** Saludos que rotan cada vez que se abre el drawer (toque de autenticidad). */
  private static readonly SALUDOS = [
    'Hola, soy Maqui', '¿En qué te ayudo hoy?', 'Aquí estoy, ¿qué necesitas?',
    'Cuéntame, ¿qué buscas?', '¿Qué revisamos hoy?', '¡Manos a la obra!',
  ];
  /** Frases del tooltip del FAB. */
  private static readonly TIPS = [
    '¿En qué te ayudo?', '¿Necesitas ayuda?', 'Pregúntame algo', 'Estoy aquí 👷',
  ];

  readonly mensajes = signal<MaquiMsg[]>([]);
  readonly consultando = signal(false);
  readonly texto = signal('');

  /** Vista activa: chat o historial. */
  readonly view = signal<'chat' | 'history'>('chat');
  readonly conversaciones = signal<MaquiConversacionItem[]>([]);

  /** Índice de rotación (sube en cada apertura) → saludo y tooltip cambian. */
  private readonly rotacion = signal(0);
  readonly saludo = computed(() =>
    SrMaquiComponent.SALUDOS[this.rotacion() % SrMaquiComponent.SALUDOS.length]);
  readonly tip = computed(() =>
    SrMaquiComponent.TIPS[this.rotacion() % SrMaquiComponent.TIPS.length]);

  /** Coach de onboarding (primera vez): se muestra si Maqui está habilitado, el
      drawer cerrado y el usuario no lo descartó antes. */
  private static readonly ONBOARDING_KEY = 'maqui_onboarding_seen';
  private readonly coachDismissed = signal(localStorage.getItem(SrMaquiComponent.ONBOARDING_KEY) === '1');
  readonly showCoach = computed(() => this.enabled() && !this.open() && !this.coachDismissed());

  /** Nudge proactivo (máx 1/día): señal de negocio real que se trae al habilitar Maqui. */
  private static readonly NUDGE_KEY = 'maqui_nudge_seen'; // guarda la fecha (yyyy-mm-dd)
  readonly nudge = signal<MaquiNudge | null>(null);
  private readonly nudgeSeen = signal(
    localStorage.getItem(SrMaquiComponent.NUDGE_KEY) === SrMaquiComponent.hoyStr());
  readonly showNudge = computed(() => this.enabled() && !!this.nudge() && !this.nudgeSeen());

  private static hoyStr(): string {
    return new Date().toISOString().slice(0, 10);
  }

  constructor() {
    // Trae el nudge UNA vez cuando Maqui queda habilitado y no se vio hoy.
    let pedido = false;
    effect(() => {
      if (this.enabled() && !this.nudgeSeen() && !pedido) {
        pedido = true;
        this.maqui.getNudge().subscribe(n => this.nudge.set(n));
      }
    });
  }

  /** CTA del nudge: navega al reporte accionable y lo marca visto por hoy. */
  verNudge(): void {
    const n = this.nudge();
    this.descartarNudge();
    if (n?.rutaAccion) {
      this.open.set(false);
      this.router.navigate([n.rutaAccion]);
    }
  }

  /** Descarta el nudge del día (no vuelve a mostrarse hasta mañana). */
  descartarNudge(): void {
    localStorage.setItem(SrMaquiComponent.NUDGE_KEY, SrMaquiComponent.hoyStr());
    this.nudgeSeen.set(true);
  }

  private conversacionId: number | null = null;
  private readonly bodyEl = viewChild<ElementRef<HTMLElement>>('body');
  private readonly inputEl = viewChild<ElementRef<HTMLInputElement>>('input');

  /** Rutas de deep-link por tipo de entidad citada. */
  private static readonly RUTAS: Record<string, string> = {
    cliente: '/clientes',
    obra: '/obras',
    equipo: '/equipos',
    'estado-pago': '/facturacion/estados-pago',
    guia: '/facturacion/guias',
    factura: '/facturacion/documentos',
  };

  /** Esc cierra el drawer (en desktop no se cierra al clickear fuera, es intencional). */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open()) this.cerrar();
  }

  abrir(): void {
    this.dismissCoach();
    this.rotacion.update(i => i + 1); // rota saludo + tooltip en cada apertura
    this.open.set(true);
    setTimeout(() => this.inputEl()?.nativeElement.focus(), 50);
  }

  /** Marca el onboarding como visto (una sola vez por usuario). */
  dismissCoach(): void {
    if (this.coachDismissed()) return;
    localStorage.setItem(SrMaquiComponent.ONBOARDING_KEY, '1');
    this.coachDismissed.set(true);
  }

  cerrar(): void {
    this.open.set(false);
  }

  nueva(): void {
    this.conversacionId = null;
    this.mensajes.set([]);
    this.texto.set('');
    this.view.set('chat');
    setTimeout(() => this.inputEl()?.nativeElement.focus(), 50);
  }

  /** Alterna entre el chat y el historial; al abrir el historial carga la lista. */
  toggleHistorial(): void {
    if (this.view() === 'history') {
      this.view.set('chat');
      return;
    }
    this.view.set('history');
    this.maqui.getConversaciones().subscribe(c => this.conversaciones.set(c));
  }

  /** Carga una conversación del historial y vuelve al chat. */
  cargarConversacion(c: MaquiConversacionItem): void {
    this.maqui.getConversacion(c.id).subscribe(msgs => {
      this.conversacionId = c.id;
      this.mensajes.set(msgs.map(m => ({
        rol: m.rol === 'USER' ? 'user' : 'assistant',
        texto: m.texto,
        sources: m.sources,
      } as MaquiMsg)));
      this.view.set('chat');
      this.scrollToBottom();
    });
  }

  enviar(pregunta: string): void {
    const q = (pregunta || '').trim();
    if (!q || this.consultando()) return;

    this.mensajes.update(m => [...m, { rol: 'user', texto: q }]);
    this.texto.set('');
    this.consultando.set(true);
    this.scrollToBottom();

    // Placeholder del asistente que se va llenando token por token (streaming).
    const bot: MaquiMsg = { rol: 'assistant', texto: '' };
    this.mensajes.update(m => [...m, bot]);

    // fetch/stream corre fuera de la zona de Angular → envolvemos en zone.run.
    this.maqui.streamPreguntar(q, this.conversacionId, {
      onDelta: (t) => this.zone.run(() => {
        this.consultando.set(false);
        bot.texto += t;
        this.refrescar();
        this.scrollToBottom();
      }),
      onConsulting: () => this.zone.run(() => this.consultando.set(true)),
      onSources: (s) => this.zone.run(() => { bot.sources = s; this.refrescar(); }),
      onSugerencias: (s) => this.zone.run(() => { bot.sugerencias = s; this.refrescar(); this.scrollToBottom(); }),
      onAction: (a) => this.zone.run(() => {
        bot.accion = a;
        bot.estado = 'propuesta';
        this.refrescar();
        this.scrollToBottom();
      }),
      onDone: (d) => this.zone.run(() => {
        this.conversacionId = d.conversacionId;
        this.consultando.set(false);
        this.refrescar();
        this.ui.refrescarUso();
      }),
      onError: (msg, conversacionId) => this.zone.run(() => {
        // La conversación pudo haberse creado antes del error: la siguiente pregunta sigue en ella.
        if (conversacionId != null) this.conversacionId = conversacionId;
        this.consultando.set(false);
        bot.texto = bot.texto ? bot.texto + '\n\n' + msg : msg;
        this.refrescar();
        this.scrollToBottom();
        // Una consulta que falla a la mitad igual gasta, y el error puede ser justamente el tope.
        this.ui.refrescarUso();
      }),
    });
  }

  /**
   * Confirma y ejecuta la acción propuesta en un mensaje (emisión real). Solo desde la tarjeta: tras un error no hay
   * «Reintentar», porque la acción pudo haberse hecho y repetirla emitiría otra guía, otro correo u otro pago.
   */
  confirmarAccion(m: MaquiMsg): void {
    if (!m.accion || m.estado !== 'propuesta') return;
    m.estado = 'confirmando';
    m.resultado = undefined;
    m.incierto = false;
    this.refrescar();
    this.maqui.confirmar(m.accion, this.conversacionId).subscribe({
      next: (r) => {
        m.resultado = r;
        m.estado = r.exito ? 'exito' : 'error';
        this.refrescar();
        this.scrollToBottom();
      },
      error: (err) => {
        m.incierto = resultadoIncierto(err?.status);
        m.resultado = {
          exito: false,
          tipo: m.accion!.tipo,
          mensaje: this.mensajeErrorAccion(err, m.incierto, this.info(m.accion!.tipo)),
        };
        m.estado = 'error';
        this.refrescar();
        this.scrollToBottom();
      },
    });
  }

  /** Lleva a la pantalla donde se ve si la acción quedó hecha. */
  revisar(m: MaquiMsg): void {
    this.router.navigate([this.info(m.accion?.tipo).revisarRuta]);
  }

  /** Traduce un error HTTP del confirmar a un mensaje claro para la card (tuteo). */
  private mensajeErrorAccion(err: any, incierto: boolean, info: MaquiAccionInfo): string {
    if (incierto) {
      return 'No recibí la respuesta, así que no sé si alcanzó a hacerse. ' + info.revisarTexto;
    }
    const backendMsg: string | undefined =
      err?.error?.message || err?.error?.mensaje || err?.error?.error;
    if (backendMsg) return backendMsg;
    const status = err?.status;
    if (status === 401 || status === 403) return 'Tu rol no permite esta acción.';
    if (status === 400) return 'Los datos de la acción no son válidos.';
    return 'No se pudo completar la acción.';
  }

  cancelarAccion(m: MaquiMsg): void {
    if (m.estado !== 'propuesta') return;
    m.estado = 'cancelado';
    this.refrescar();
  }

  /** Toca una respuesta rápida (quick reply): la envía como mensaje y quita los chips. */
  responder(m: MaquiMsg, texto: string): void {
    if (this.consultando()) return;
    m.sugerencias = undefined;
    this.refrescar();
    this.enviar(texto);
  }

  /** Preview de fuente al pasar el mouse (peek antes de navegar). Posición fixed. */
  readonly srcPeek = signal<{ src: MaquiSource; top: number; left: number } | null>(null);

  private static readonly TIPO_LABEL: Record<string, string> = {
    cliente: 'Cliente', obra: 'Obra', equipo: 'Equipo',
    'estado-pago': 'Estado de pago', factura: 'Documento', pantalla: 'Guía de uso',
  };
  tipoLabel(t: string): string { return SrMaquiComponent.TIPO_LABEL[t] ?? t; }

  peekFuente(src: MaquiSource | null, ev?: MouseEvent): void {
    if (!src || !ev) { this.srcPeek.set(null); return; }
    const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    this.srcPeek.set({ src, top: r.top, left: r.left + r.width / 2 });
  }

  abrirFuente(src: MaquiSource): void {
    this.srcPeek.set(null);
    // La guía de uso lleva a la pantalla donde se empieza, no al detalle de una entidad.
    if (src.ruta) { this.router.navigateByUrl(src.ruta); return; }
    this.navegar(src.entityType, src.entityId);
  }

  abrirDocumento(res: MaquiConfirmarResponse): void {
    if (res.entityType && res.entityId) this.navegar(res.entityType, res.entityId);
  }

  private navegar(entityType: string, entityId?: number): void {
    const base = SrMaquiComponent.RUTAS[entityType];
    // Navega al detalle sin cerrar el drawer.
    if (base && entityId) this.router.navigate([base, entityId]);
  }

  /** Fuerza el re-render tras mutar un mensaje del hilo (signal por referencia). */
  private refrescar(): void {
    this.mensajes.update(l => [...l]);
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.bodyEl()?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 60);
  }
}
